import React, { useEffect, useRef } from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LENDERS, lenderHref, financingMicrocopy } from '../../config/financing';
import { HeroPicture, CtaBand } from '../Shared';
import { Breadcrumbs } from '../Breadcrumbs';
import { CITY_LIST } from '../../data/locations';
import { trackFinancingClick } from '../../lib/analytics';

// All typography is presentation only: slicing retains the original inter-word space.
export const DisplayHeading = ({ text, split, as: Tag = 'h2', className = '', ...props }) => {
  if (Array.isArray(split)) {
    const starts = [0, ...split.map((phrase) => text.indexOf(phrase)).filter((index) => index > 0), text.length];
    return <Tag className={`idb-display ${className}`} {...props}>{starts.slice(0, -1).map((start, index) => <span key={start} className={index === starts.length - 2 ? 'idb-display-accent' : 'idb-display-sentence'}>{text.slice(start, starts[index + 1])}</span>)}</Tag>;
  }
  let boundary = typeof split === 'number' ? split : split ? text.indexOf(split) : -1;
  if (boundary < 1 && !split) {
    const sentences = [...text.matchAll(/[.!?]\s+(?=\S)/g)];
    if (sentences.length) boundary = sentences[sentences.length - 1].index + sentences[sentences.length - 1][0].length;
  }
  return <Tag className={`idb-display ${className}`} {...props}>{boundary > 0 ? <>{text.slice(0, boundary)}<span className="idb-display-accent">{text.slice(boundary)}</span></> : text}</Tag>;
};

// Visible by default, including SSR/no-JS. Never hide anything already in view.
export const FamilyReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!el || media.matches || !window.IntersectionObserver || el.getBoundingClientRect().top < window.innerHeight) return;
    const show = () => { el.classList.remove('idb-family-reveal-pending'); };
    el.classList.add('idb-family-reveal-pending');
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { show(); io.disconnect(); }
    }, { threshold: 0, rootMargin: '0px 0px 32px 0px' });
    io.observe(el);
    media.addEventListener('change', show);
    const failSafe = window.setTimeout(show, 2500);
    return () => { show(); io.disconnect(); clearTimeout(failSafe); media.removeEventListener('change', show); };
  }, []);
  return <div ref={ref} className={`idb-family-reveal ${className}`} style={delay ? { transitionDelay: `${delay}s` } : undefined}>{children}</div>;
};

export const FamilySection = ({ children, className = '', ...props }) => <section className={`idb-family-section ${/bg-\[#(?:121417|1B2026)\]/.test(className) ? 'idb-tone-dark' : ''} ${className}`} {...props}><div className="idb-family-container">{children}</div></section>;

export const FamilySectionTitle = ({ title, sub, split, dark = false, className = '' }) => <FamilyReveal className={`idb-section-heading ${dark ? 'idb-tone-dark' : ''} ${className}`}><DisplayHeading text={title} split={split ?? phraseSplit(title)} />{sub && <p className="idb-section-intro">{sub}</p>}</FamilyReveal>;

// Decorate the existing anchor, preserving every handler and interactive attribute.
export const PillButton = ({ children, variant = 'primary', arrow = false, className = '' }) => {
  const child = React.Children.only(children);
  const props = { className: `${child.props.className || ''} idb-pill idb-pill-${variant} ${className}` };
  return arrow ? React.cloneElement(child, props, <>{child.props.children}<ArrowRight className="idb-pill-arrow" aria-hidden="true" /></>) : React.cloneElement(child, props);
};

export const PhotoTile = ({ children, variant = 'service' }) => {
  const child = React.Children.only(children);
  return React.cloneElement(child, { className: `${child.props.className || ''} idb-photo-tile idb-photo-tile-${variant}` });
};

export const StepRow = ({ children, vertical = false, className = '', as: Tag = 'div', ...props }) => <Tag className={`idb-step-row ${vertical ? 'idb-step-row-vertical' : ''} ${className}`} {...props}>{children}</Tag>;

export const CityMarquee = ({ cities = CITY_LIST.map((city) => city.city) }) => <div className="idb-city-marquee" aria-hidden="true"><div className="idb-city-marquee-track">{[0, 1].map((copy) => <div className="idb-city-marquee-group" key={copy}>{cities.map((city) => <span className="idb-city-marquee-name" key={city}>{city}<ArrowUpRight /></span>)}</div>)}</div></div>;

// Marketing-only rendering of the existing lender config. Shared Lenders.js stays untouched,
// so neither calculator-result lender buttons nor financing-page cards inherit these styles.
export const FamilyLenderButtons = ({ placement = 'home_band', testId = 'home-lenders' }) => <div data-testid={testId} data-placement={placement} data-lender-count={LENDERS.length} className="idb-family-lenders"><div className="idb-family-lender-grid">{LENDERS.map((l) => <a key={l.id} href={lenderHref(l, null)} target="_blank" rel="noopener sponsored" onClick={() => { trackFinancingClick(l.id, placement); }} data-testid={`lender-button-${l.id}`} data-lender={l.id} className="idb-pill idb-pill-secondary-dark"><span className="leading-tight">{l.buttonLabel}</span> <ArrowUpRight className="h-4 w-4 shrink-0 opacity-80" aria-hidden="true" /></a>)}</div><p className="idb-family-lender-disclosure" data-testid={`${testId}-microcopy`}>{financingMicrocopy(LENDERS)}</p></div>;

export const FamilyFinancingLink = ({ className = '', testId = 'home-financing-how-link' }) => <Link to="/financing" className={`idb-financing-link ${className}`} data-testid={testId}>How financing works <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" /></Link>;


// DESIGN-02: extend the existing family, preserving PageHeader's heading roles and attributes.
const phraseSplit = (text) => {
  if (/[.!?]\s+\S/.test(text)) return undefined;
  for (const phrase of [' before ', ' without ', ' across ', ' for ', ' with ', ' in ']) {
    const at = text.lastIndexOf(phrase);
    if (at > 8) return at + 1;
  }
  const middle = text.indexOf(' ', Math.floor(text.length * .45));
  return middle > 0 ? middle + 1 : undefined;
};
export const PageHero = ({ heading, title, sub, image, crumbs }) => <div className="idb-page-hero idb-tone-dark idb-grain idb-blueprint idb-blueprint-dark">
  {image && <HeroPicture src={image} className="idb-page-hero-image" loading="eager" />}
  <div className="idb-page-hero-scrim" />
  <div className="idb-family-container idb-page-hero-content">
    {crumbs && <Breadcrumbs crumbs={crumbs} tone="dark" className="mb-5" />}
    {heading ? <><h1 className="idb-eyebrow" data-testid="pageheader-h1">{heading}</h1><DisplayHeading as="p" text={title} split={phraseSplit(title)} className="idb-page-hero-title" data-testid="pageheader-title" /></> : <DisplayHeading as="h1" text={title} split={phraseSplit(title)} className="idb-page-hero-title" data-testid="pageheader-title" />}
    {sub && <p className="idb-page-hero-lede">{sub}</p>}
  </div>
  <div className="absolute bottom-0 inset-x-0 idb-gradient-hairline z-10" />
</div>;

export const FamilyPageEnd = (props) => <><CityMarquee cities={CITY_LIST.map((city) => city.city)} /><CtaBand {...props} /></>;
